import {Injectable, NgZone} from '@angular/core';
import {ModalController, NavController} from '@ionic/angular/standalone';
import { HttpClient, HttpParams } from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {environment} from '../../environments/environment';
import {LocalStorageService} from './local-storage.service';
import {NotificationService} from './notification.service';
import {SessionExpiryModalComponent} from '../shared/modals/session-expiry/session-expiry-modal.component';
import {PathEnum} from '../shared/enums/path-enum';
import {AuthMode} from '../shared/enums/auth-mode.enum';
import {TranslocoService} from '@jsverse/transloco';

const THRESHOLD_SECONDS = 60;

@Injectable({providedIn: 'root'})
export class TokenExpiryService {

  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private tokenExp: number | null = null;
  private modalOpen = false;

  constructor(
    private modalController: ModalController,
    private localStorageService: LocalStorageService,
    private notificationService: NotificationService,
    private navCtrl: NavController,
    private http: HttpClient,
    private ngZone: NgZone,
    private transloco: TranslocoService,
  ) {}

  startMonitoring(accessToken: string): void {
    this.stopMonitoring();

    const exp = this.decodeExp(accessToken);
    if (!exp) {
      return;
    }
    this.tokenExp = exp;

    const nowSec = Math.floor(Date.now() / 1000);
    const remaining = exp - nowSec;

    if (remaining <= 0) {
      this.performLogout();
      return;
    }

    if (remaining <= THRESHOLD_SECONDS) {
      this.openModal(remaining);
      return;
    }

    const delay = (remaining - THRESHOLD_SECONDS) * 1000;
    this.timeoutId = setTimeout(() => {
      this.ngZone.run(() => this.onThresholdReached());
    }, delay);
  }

  stopMonitoring(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.tokenExp = null;
  }

  private onThresholdReached(): void {
    // Recalculate from exp to handle background tab throttling
    if (!this.tokenExp) {
      return;
    }
    const nowSec = Math.floor(Date.now() / 1000);
    const remaining = this.tokenExp - nowSec;

    if (remaining <= 0) {
      this.performLogout();
      return;
    }

    this.openModal(Math.min(remaining, THRESHOLD_SECONDS));
  }

  private async openModal(seconds: number): Promise<void> {
    if (this.modalOpen) {
      return;
    }
    this.modalOpen = true;

    const modal = await this.modalController.create({
      component: SessionExpiryModalComponent,
      componentProps: {secondsRemaining: seconds},
      backdropDismiss: false,
      cssClass: 'generic-modal',
      htmlAttributes: { 'aria-labelledby': 'modal-title' }
    });
    await modal.present();

    const {role} = await modal.onDidDismiss();
    this.modalOpen = false;

    if (role === 'extend') {
      await this.handleExtend();
    } else {
      await this.performLogout();
    }
  }

  private async handleExtend(): Promise<void> {
    try {
      const refreshToken = this.localStorageService.getRefreshToken();
      if (!refreshToken) {
        await this.performLogout();
        return;
      }

      const tokens = await this.refreshFromKeycloak(refreshToken);

      // Save new tokens
      await firstValueFrom(this.localStorageService.setAccessToken$(tokens.access_token));
      await firstValueFrom(this.localStorageService.setRefreshToken$(tokens.refresh_token));

      const authMode = this.localStorageService.getAuthMode();
      if (authMode === AuthMode.SPID) {
        await firstValueFrom(
          this.localStorageService.setSpidAuth$(tokens.access_token, tokens.refresh_token, tokens.expires_in)
        );
      }

      // Restart monitoring with new token
      this.startMonitoring(tokens.access_token);
    } catch {
      await this.performLogout();
    }
  }

  private async refreshFromKeycloak(refreshToken: string): Promise<{access_token: string; refresh_token: string; expires_in: number}> {
    const tokenUrl = `${environment.spid.authority}/protocol/openid-connect/token`;

    const body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('client_id', environment.spid.clientId)
      .set('refresh_token', refreshToken);

    return firstValueFrom(
      this.http.post<{access_token: string; refresh_token: string; expires_in: number}>(tokenUrl, body.toString(), {
        headers: {'Content-Type': 'application/x-www-form-urlencoded'},
      })
    );
  }

  private async performLogout(): Promise<void> {
    this.stopMonitoring();
    this.localStorageService.cleanPreferenceToken$().subscribe();
    await this.notificationService.closeAll();
    await this.navCtrl.navigateRoot(PathEnum.LOGIN, {
      replaceUrl: true,
      state: {sessionExpired: true},
    });
    this.notificationService.openToast({
      message: this.transloco.translate('common.toast.session_expired_login'),
      color: 'warning',
    });
  }

  private decodeExp(token: string): number | null {
    try {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      return decoded.exp ?? null;
    } catch {
      return null;
    }
  }
}
